import React, {useState, useEffect} from 'react';
import Config from "./core/config";
import sgeImg from "./img/sge-sistemas-logo.png";
import {isUserAdmin} from "./utils/requestUtils";
import {clearAuthSession, setAuthSession} from "./utils/auth";
import './Login.css';

const apiUrl = Config.apiUrl;

// Depoimentos reais (5 estrelas) curados do feedback dos clientes, anonimizados.
const TESTIMONIALS = [
    {
        text: 'O sistema é super fácil de usar. Muito intuitivo. Permite trabalhar de qualquer '
            + 'lugar com segurança, 24h por dia. E o suporte atende prontamente qualquer necessidade.',
        author: 'Cliente SGE',
    },
    {
        text: 'Excelente! Muito intuitivo e prático.',
        author: 'Cliente SGE',
    },
    {
        text: 'Gosto da maneira de organização. Não tenho o que reclamar.',
        author: 'Cliente SGE',
    },
];

const WHATSAPP_HREF = 'https://wa.me/5535984314901?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20sistema%20SGE.';

const WhatsAppLink = ({className = ''}) => (
    <a
        className={`login-whatsapp ${className}`.trim()}
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noopener noreferrer"
    >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19.05 4.91A9.82 9.82 0 0012.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 004.78 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.02zM12.04 20.15h-.01a8.2 8.2 0 01-4.18-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 01-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.2 8.2 0 015.83 2.42 8.18 8.18 0 012.41 5.83c0 4.54-3.7 8.4-8.25 8.4zm4.52-6.16c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.16.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.01-.38.11-.51.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29z"/>
        </svg>
        Quero conhecer o sistema
    </a>
);

const TestimonialsCarousel = () => {
    const [index, setIndex] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((prev) => (prev + 1) % TESTIMONIALS.length);
        }, 5500);
        return () => clearInterval(timer);
    }, []);

    const current = TESTIMONIALS[index];

    return (
        <figure className="login-quote">
            <div className="login-quote-stars" aria-label="Avaliação 5 de 5 estrelas">
                {'★★★★★'}
            </div>
            <blockquote key={index} className="login-quote-text" aria-live="polite">
                “{current.text}”
            </blockquote>
            <figcaption className="login-quote-author">{current.author}</figcaption>
            <div className="login-quote-dots">
                {TESTIMONIALS.map((t, idx) => (
                    <button
                        key={t.text}
                        type="button"
                        className={idx === index ? 'is-active' : ''}
                        onClick={() => setIndex(idx)}
                        aria-label={`Depoimento ${idx + 1} de ${TESTIMONIALS.length}`}
                    />
                ))}
            </div>
        </figure>
    );
};

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [hashLoginPending, setHashLoginPending] = useState(false);
    // No mobile: cliente que nunca logou neste navegador vê a apresentação; quem já
    // logou ao menos uma vez vai direto ao formulário. No desktop ambos aparecem.
    const [mobileView, setMobileView] = useState(() => {
        try {
            return window.localStorage.getItem('sgeVisitedBefore') === '1' ? 'login' : 'presentation';
        } catch (e) {
            return 'login';
        }
    });

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const reason = params.get('reason');
        const stored = sessionStorage.getItem('loginMessage');
        if (stored) {
            setLoginError(stored);
            sessionStorage.removeItem('loginMessage');
        } else if (reason === 'OUTSIDE_WORK_HOURS') {
            setLoginError('Acesso permitido apenas no horário de trabalho definido para sua empresa.');
        } else if (reason === 'AUTH_TOKEN_EXPIRED') {
            setLoginError('Sessão expirada. Faça login novamente.');
        }
        // Garante que qualquer sessão antiga não fique pendurada na tela de login.
        clearAuthSession();
    }, []);

    const finishLogin = async (token, userData, expiresInMillis) => {
        if (!token || !userData) {
            setLoginError('Resposta de login inválida.');
            return;
        }

        setAuthSession(token, userData, expiresInMillis);
        try {
            window.localStorage.setItem('sgeVisitedBefore', '1');
        } catch (e) {
            // ignora indisponibilidade de localStorage
        }

        try {
            const userAdmin = await isUserAdmin();

            const rawRedirect = sessionStorage.getItem('redirectAfterLogin');
            sessionStorage.removeItem('redirectAfterLogin');

            // Só aceita paths internos (começando com /, exceto //) e que não voltem para /login.
            const isInternalPath = !!rawRedirect
                && rawRedirect.startsWith('/')
                && !rawRedirect.startsWith('//');
            const pointsToLogin = !!rawRedirect
                && (rawRedirect === '/login' || rawRedirect.startsWith('/login?') || rawRedirect.startsWith('/login/'));
            const safeRedirect = isInternalPath && !pointsToLogin ? rawRedirect : null;
            const redirectIsAdmin = safeRedirect != null && safeRedirect.startsWith('/admin');

            // Usa o redirect guardado quando ele bate com o papel do usuário; caso contrário,
            // manda o admin para /admin e o usuário comum para /app.
            let target;
            if (userAdmin) {
                target = redirectIsAdmin ? safeRedirect : '/admin';
            } else {
                target = safeRedirect && !redirectIsAdmin ? safeRedirect : '/app';
            }

            setLoginError(null);
            window.location.assign(target);
        } catch (error) {
            setLoginError('Erro fazer login.');
        }
    };

    useEffect(() => {
        const hash = new URLSearchParams(window.location.search).get('hash');
        if (!hash) {
            return;
        }
        let cancelled = false;
        setHashLoginPending(true);
        (async () => {
            try {
                clearAuthSession();
                const response = await fetch(`${apiUrl}/api/users/login-by-hash?hash=${encodeURIComponent(hash)}`);
                if (!response.ok) {
                    const msg = await response.text();
                    if (!cancelled) {
                        setLoginError(msg || 'Convite inválido ou expirado.');
                    }
                    return;
                }
                const payload = await response.json();
                await finishLogin(payload.token, payload.user, payload.expiresInMillis);
            } catch (error) {
                console.error('Erro ao fazer login por convite:', error);
                if (!cancelled) {
                    setLoginError('Erro ao validar o convite. Tente novamente.');
                }
            } finally {
                if (!cancelled) {
                    setHashLoginPending(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLogin = async () => {
        try {
            if (!email) {
                setLoginError('Informe o e-mail ou usuário.');
                return;
            }
            if (!password) {
                setLoginError('Informe a senha.');
                return;
            }

            setLoading(true);
            const basicAuthToken = btoa(`${email}:${password}`);

            const response = await fetch(`${apiUrl}/api/users/login`, {
                method: 'GET',
                headers: {
                    'Authorization': `Basic ${basicAuthToken}`
                },
            });

            if (response.ok) {
                const payload = await response.json();
                await finishLogin(payload.token, payload.user, payload.expiresInMillis);
            } else if (response.status === 403) {
                try {
                    const data = await response.json();
                    setLoginError(data.message || 'Acesso permitido apenas no horário de trabalho definido para sua empresa.');
                } catch (e) {
                    setLoginError('Acesso permitido apenas no horário de trabalho definido para sua empresa.');
                }
            } else {
                setLoginError('E-mail ou senha inválidos');
            }
        } catch (error) {
            console.error('Erro ao fazer login:', error);
            setLoginError('Erro ao fazer login. Tente novamente mais tarde.');
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        if (!loading) handleLogin();
    };

    return (
        <div className={`login-root login-root--${mobileView}`}>
            <aside className="login-brand">
                <span className="login-brand-grain" aria-hidden="true"/>
                <button
                    type="button"
                    className="login-client-area"
                    onClick={() => setMobileView('login')}
                >
                    Área do cliente
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                <div className="login-brand-mark">
                    <img src={sgeImg} alt="SGE — Sua Gestão Empresarial"/>
                </div>
                <div className="login-brand-copy">
                    <h1 className="login-brand-title">
                        Mais tempo para vender.<br/>Menos burocracia.
                    </h1>
                    <p className="login-brand-sub">
                        Centralize a gestão da sua empresa em um sistema prático, intuitivo e feito
                        para a sua realidade.
                    </p>
                    <TestimonialsCarousel/>
                    <WhatsAppLink className="login-whatsapp--brand"/>
                </div>
                <div className="login-brand-foot">© {new Date().getFullYear()} SGE · Sua Gestão Empresarial</div>
            </aside>

            <main className="login-panel">
                <div className="login-card">
                    <img className="login-card-logo" src={sgeImg} alt="SGE — Sua Gestão Empresarial"/>
                    <p className="login-eyebrow">Seja bem-vindo</p>
                    <h2 className="login-title">Entrar na conta</h2>
                    <p className="login-subtitle">Informe suas credenciais para continuar.</p>

                    {loginError && (
                        <div className="login-error" role="alert">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                                <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="12" cy="16.5" r="1.1" fill="currentColor"/>
                            </svg>
                            <span>{loginError}</span>
                        </div>
                    )}

                    {hashLoginPending && (
                        <p className="login-subtitle">
                            <span className="login-spinner" aria-hidden="true"/> Entrando com o convite…
                        </p>
                    )}

                    <form className="login-form" onSubmit={onSubmit} style={{display: hashLoginPending ? 'none' : 'block'}}>
                        <div className="login-field">
                            <input
                                id="login-email"
                                type="text"
                                placeholder=" "
                                autoComplete="username"
                                autoCapitalize="none"
                                spellCheck={false}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <label htmlFor="login-email">E-mail ou usuário</label>
                        </div>

                        <div className="login-field login-field--password">
                            <input
                                id="login-password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder=" "
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <label htmlFor="login-password">Senha</label>
                            <button
                                type="button"
                                className="login-toggle"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                tabIndex={-1}
                            >
                                {showPassword ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M10.6 10.6a2 2 0 002.8 2.8M9.4 5.2A9.7 9.7 0 0112 5c5 0 9 4.5 9 7 0 .9-.8 2.3-2.2 3.6M6.2 6.3C3.9 7.7 3 9.3 3 12c0 2.2 4 7 9 7 1 0 1.9-.2 2.8-.5"
                                            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                        <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"
                                            stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                                        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                )}
                            </button>
                        </div>

                        <button className="login-submit" type="submit" disabled={loading}>
                            {loading ? <><span className="login-spinner" aria-hidden="true"/>Entrando…</> : 'Entrar'}
                        </button>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default Login;
